import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';

// Public landing for the sales/demo flow (/demo, no auth required): spins up
// a disposable demo org + session server-side, then hard-navigates home so
// route guards + a fresh query cache pick up the new session (mirrors
// SsoHandoff's hard-nav-after-token-exchange pattern).
export function Demo(): JSX.Element {
  const [error, setError] = useState(false);
  const [pending, setPending] = useState(false);
  // Guard against React 18 StrictMode double-invoke spinning up two demo orgs.
  const ran = useRef(false);

  function start(): void {
    setError(false);
    setPending(true);
    void (async () => {
      try {
        await useAuthStore.getState().startDemo();
        window.location.assign('/');
      } catch {
        setError(true);
        setPending(false);
      }
    })();
  }

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    start();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      {error ? (
        <div
          role="alert"
          className="w-full max-w-sm flex flex-col items-center gap-3 rounded-3xl bg-[hsl(var(--error-container))] px-5 py-5 text-center text-sm text-destructive"
        >
          <span>{dv.demo.error}</span>
          <button type="button" onClick={start} disabled={pending} className="btn-primary btn-sm">
            {dv.demo.retry}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-muted-foreground" role="status" aria-live="polite">
          <span
            aria-hidden
            className="size-6 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
          <span className="text-sm">{dv.demo.preparing}</span>
        </div>
      )}
    </div>
  );
}
